import {
    ApiAuthAppSessionResSchema,
    validators,
} from "@bridge-applications/pb-types";
import { z } from "zod";

const STORAGE_KEY = "pbtg.auth-identity.v1";

const CachedAuthIdentitySchema = z.object({
    schemaVersion: z.literal(1),
    userId: z.uuid(),
    telegramUserId: validators.telegramId(),
    accountCreatedAt: ApiAuthAppSessionResSchema.shape.accountCreatedAt,
});

export type CachedAuthIdentity = Pick<
    z.infer<typeof CachedAuthIdentitySchema>,
    "userId" | "telegramUserId" | "accountCreatedAt"
>;

// Current authenticated metadata remains usable when browser storage is blocked.
// It is only an analytics/cache hint, never authentication or eligibility authority.
let sessionIdentity: CachedAuthIdentity | null = null;

export function cacheAuthIdentity(identity: CachedAuthIdentity): void {
    const parsed = CachedAuthIdentitySchema.safeParse({
        schemaVersion: 1,
        ...identity,
    });
    if (!parsed.success) return;
    sessionIdentity = parsed.data;
    if (typeof window === "undefined") return;

    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(parsed.data),
        );
    } catch {
        // Storage can be blocked or unavailable.
    }
}

export function readCachedAuthIdentity(): CachedAuthIdentity | null {
    if (sessionIdentity) return sessionIdentity;
    if (typeof window === "undefined") return null;

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const parsed = CachedAuthIdentitySchema.safeParse(JSON.parse(raw));
        if (!parsed.success) {
            clearCachedAuthIdentity();
            return null;
        }

        return {
            userId: parsed.data.userId,
            telegramUserId: parsed.data.telegramUserId,
            accountCreatedAt: parsed.data.accountCreatedAt,
        };
    } catch {
        clearCachedAuthIdentity();
        return null;
    }
}

export function readCachedAuthIdentityForTelegram(
    expectedTelegramUserId: number | undefined,
): CachedAuthIdentity | null {
    if (expectedTelegramUserId === undefined) return null;

    const identity = readCachedAuthIdentity();
    if (!identity) return null;

    if (identity.telegramUserId !== expectedTelegramUserId) {
        clearCachedAuthIdentity();
        return null;
    }

    return identity;
}

export function clearCachedAuthIdentity(): void {
    sessionIdentity = null;
    if (typeof window === "undefined") return;

    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Storage can be blocked or unavailable.
    }
}

/** A refreshed authenticated identity wins; old metadata survives only for the same account. */
export function reconcileAuthIdentity(
    incoming: CachedAuthIdentity,
    cached: CachedAuthIdentity | null,
): CachedAuthIdentity {
    return {
        ...incoming,
        accountCreatedAt:
            incoming.accountCreatedAt ??
            (cached?.userId === incoming.userId &&
            cached.telegramUserId === incoming.telegramUserId
                ? cached.accountCreatedAt
                : undefined),
    };
}

/** Browser fallback may use metadata confirmed by authentication in this document.
 * Persistent metadata still requires a matching Telegram launch identity.
 */
export function readAccountAnalyticsIdentity(
    expectedTelegramUserId: number | undefined,
): CachedAuthIdentity | null {
    return expectedTelegramUserId === undefined
        ? sessionIdentity
        : readCachedAuthIdentityForTelegram(expectedTelegramUserId);
}
